import './myprofile.css';
import zini from '../../assets/profile_pic.png';
import JourneyCard from './JourneyCard';
import BottomNav from './BottomNav';

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M4 20h4L19 9l-4-4L4 16v4Z" stroke="#ffffff" strokeWidth="2" strokeLinejoin="round" />
      <path d="M14 6l4 4" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function StarIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M12 3l2.6 5.3 5.9.9-4.3 4.1 1 5.8L12 17.6 6.8 20l1-5.8L3.5 10l5.9-.9L12 3Z" fill="#ffb238" />
    </svg>
  );
}

function CafeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M5 9h11v5a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4V9Z" stroke="#6c4ce8" strokeWidth="2" strokeLinejoin="round" />
      <path d="M16 10h2.2a2.3 2.3 0 0 1 0 4.6H16" stroke="#6c4ce8" strokeWidth="2" strokeLinecap="round" />
      <path d="M8 3.5c-.6 1 .6 1.8 0 2.8M11.5 3.5c-.6 1 .6 1.8 0 2.8" stroke="#6c4ce8" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
      <path d="M9 5l7 7-7 7" stroke="#6c4ce8" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GiftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <rect x="4" y="10" width="16" height="10" rx="1.6" stroke="#17b85a" strokeWidth="2" />
      <path d="M3 8.5h18V11H3zM12 8.5V20" stroke="#17b85a" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 8.5C10.5 5.5 6 5.5 6 8.5c0 0 3.5 0 6 0Zm0 0c1.5-3 6-3 6 0 0 0-3.5 0-6 0Z" stroke="#17b85a" strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

// Builds the displayed name/username from the authenticated Dealzta user
// (App.js's `authUser`, unchanged since Step 5A — the exact object Dealzta's
// login returned) instead of any hardcoded placeholder. Only reads fields
// Dealzta is already documented elsewhere in this app to return
// (DealztaAuthMiddleware's resolveLocalUser(), the /auth/me bridge route):
// user_id, username, first_name, last_name — never invents a new field.
// 'Guest' is a safe fallback for the brief window before/if authUser isn't
// available yet, never presented as if it were the real account.
function getDisplayName(authUser) {
  if (!authUser) return 'Guest';
  const fullName = [authUser.first_name, authUser.last_name].filter(Boolean).join(' ').trim();
  return fullName || authUser.username || 'Guest';
}

function getDisplayUsername(authUser) {
  if (!authUser || !authUser.username) return '';
  return `@${authUser.username}`;
}

export default function MyProfile({
  onBack,
  collectedStars = 0,
  totalStars = 3,
  onEditProfile,
  onViewAllJourneys,
  onSelectJourney,
  onNavHome,
  onNavJourney,
  onNavShops,
  // The authenticated Dealzta user (App.js's existing authUser) — the
  // source of this screen's identity. null only while auth state is
  // genuinely unavailable, never a reason to fall back to fake data.
  authUser = null,
  // Derived server-side (Step 5H) from the authenticated user's own
  // user_missions — never hardcoded. null/[] mean "no in-progress journey
  // yet" / "nothing completed yet", both genuine states, not a load failure.
  currentJourney = null,
  completedJourneys = [],
}) {
  const displayName = getDisplayName(authUser);
  const displayUsername = getDisplayUsername(authUser);

  const stats = [
    { key: 'stars', icon: <StarIcon />, value: collectedStars, label: 'Stars' },
    { key: 'shops', icon: <CafeIcon />, value: collectedStars, label: 'Shops' },
    { key: 'rewards', icon: <GiftIcon />, value: totalStars > 0 && collectedStars >= totalStars ? 1 : 0, label: 'Rewards' },
  ];

  return (
    <div className="myprofile-screen">
      <div className="myprofile-frame">
        <div className="myprofile-content">
          <div className="myprofile-top">
            <button type="button" className="myprofile-back-btn" aria-label="Go back" onClick={onBack}>
              <ArrowLeftIcon />
            </button>
            <h1 className="myprofile-title">My Profile</h1>
            <span className="myprofile-top-spacer" />
          </div>

          <div className="myprofile-card">
            <div className="myprofile-header">
              <div className="myprofile-avatar-wrap">
                {/* Dealzta's profile_photo (from the stored login user), else the
                    built-in avatar — also used if the photo URL fails to load. */}
                <img
                  src={authUser?.profile_photo || zini}
                  alt={displayName}
                  className="myprofile-avatar"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = zini;
                  }}
                />
                {/* <button
                  type="button"
                  className="myprofile-avatar-edit"
                  aria-label="Edit profile picture"
                  onClick={onEditProfile}
                >
                  <EditIcon />
                </button> */}
              </div>
              <div className="myprofile-header-text">
                <h2 className="myprofile-name">{displayName}</h2>
                <p className="myprofile-username">{displayUsername}</p>
              </div>
            </div>

            <div className="myprofile-stats">
              {stats.map((stat) => (
                <div className="myprofile-stat" key={stat.key}>
                  <span className="myprofile-stat-icon">{stat.icon}</span>
                  <span className="myprofile-stat-value">{stat.value}</span>
                  <span className="myprofile-stat-label">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="myprofile-journey-section">
            <div className="myprofile-section-header">
              <h3 className="myprofile-section-title">Current Journey</h3>
              <button type="button" className="myprofile-viewall-btn" onClick={onViewAllJourneys}>
                View All
                <ChevronRightIcon />
              </button>
            </div>

            {currentJourney && (
              <div className="myprofile-journey-list">
                <JourneyCard
                  journey={currentJourney}
                  onClick={() => onSelectJourney?.(currentJourney.id)}
                />
              </div>
            )}
          </div>

          {completedJourneys.length > 0 && (
            <div className="myprofile-journey-section">
              <div className="myprofile-section-header">
                <h3 className="myprofile-section-title">Completed Journeys</h3>
              </div>

              <div className="myprofile-journey-list">
                {completedJourneys.map((journey) => (
                  <JourneyCard
                    key={journey.id}
                    journey={journey}
                    onClick={() => onSelectJourney?.(journey.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <BottomNav
          active="profile"
          onHome={onNavHome}
          onJourney={onNavJourney}
          onShops={onNavShops}
          onProfile={() => {}}
        />
      </div>
    </div>
  );
}
